import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { User } from "@/types";

const CHANGE_EMAIL_MUTATION = gql`
  mutation ChangeEmail($newEmail: String!, $currentPassword: String!) {
    changeEmail(newEmail: $newEmail, currentPassword: $currentPassword) {
      user {
        id
        email
        displayName
        bio
        avatarUrl
        rating
        ratingsCount
        createdAt
        admin
        activeListingsCount
      }
      errors
    }
  }
`;

export function ChangeEmailPage() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (vars: { newEmail: string; currentPassword: string }) =>
      graphqlClient.request<{
        changeEmail: { user: User | null; errors: string[] };
      }>(CHANGE_EMAIL_MUTATION, vars),
    onSuccess: (data) => {
      if (data.changeEmail.user) {
        updateUser(data.changeEmail.user);
        setErrors([]);
        setSuccess(true);
        setNewEmail("");
        setCurrentPassword("");
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setErrors(data.changeEmail.errors);
        setSuccess(false);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);
    mutation.mutate({ newEmail, currentPassword });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center gap-3">
        <Link
          to="/profile/edit"
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {t("settings.title")}
        </Link>
        <span className="text-sm text-gray-400">/</span>
        <h1 className="text-2xl font-bold text-gray-900">
          {t("settings.change_email.title")}
        </h1>
      </div>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          {t("settings.change_email.success")}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.change_email.current_email")}
            </span>
            <span className="text-sm text-gray-500">{user?.email}</span>
          </div>

          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="newEmail" className="text-sm font-semibold text-gray-900">
              {t("settings.change_email.new_email")}
            </label>
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="input sm:w-72"
              required
            />
          </div>

          <div className="flex flex-col gap-2 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="currentPassword" className="text-sm font-semibold text-gray-900">
              {t("settings.change_email.password_confirm")}
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input sm:w-72"
              required
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary"
          >
            {mutation.isPending
              ? t("settings.change_email.submitting")
              : t("settings.change_email.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
